import { useRef, useState } from 'react';
import { Modal, message } from 'antd';
import { BaseButton } from '../UI/BaseButton';
import { useActiveOrganization } from '../../common/auth';
import { analytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';
import { useSubmitSelfSortMutation, SelfSortSubmitPayload } from '../../serverState/selfSort';
import { COMPANY_SIZE_OPTIONS, REFERRAL_OPTIONS, TOOL_OPTIONS, SelfSortVariant } from './selfSort.constants';
import AccordionQuestion from './questions/AccordionQuestion';
import SingleSelectQuestion from './questions/SingleSelectQuestion';
import RoleSelect from './questions/RoleSelect';

type Props = {
  open: boolean;
  onCancel: () => void;
  variant: SelfSortVariant;
  /**
   * True when this user already completed self-sort in another org (role is a
   * per-user answer, collected once). An owner re-prompted for a NEW org's
   * firmographics then skips the role question and only writes firmographics.
   */
  roleKnown?: boolean;
};

/**
 * Onboarding modal. Owner collects org firmographics (company size, referral,
 * tool) + role; member collects role only. A returning owner (`roleKnown`) is
 * asked only the three per-org firmographics. Skippable via X (no write). Submit
 * is disabled until the required answers are present and while a write is in flight.
 */
export default function SelfSortModal({ open, onCancel, variant, roleKnown = false }: Props) {
  const isOwner = variant === 'owner';
  // Owner who already answered role in another org: drop the role question, ask
  // only the per-org firmographics.
  const collectRole = !isOwner || !roleKnown;
  const { data: activeOrganization } = useActiveOrganization();
  const [messageApi, contextHolder] = message.useMessage();
  const submit = useSubmitSelfSortMutation();

  const [companySize, setCompanySize] = useState<string | null>(null);
  const [jobRole, setJobRole] = useState<string | null>(null);
  const [referralSource, setReferralSource] = useState<string | null>(null);
  const [previousTool, setPreviousTool] = useState<string | null>(null);
  const [openIndex, setOpenIndex] = useState(0);
  // True when the role header was pressed while its question was open. Pressing the
  // header dismisses the role dropdown (mousedown), which collapses the question via
  // onRequestCollapse — but the following header click's onToggle would then read the
  // already-collapsed openIndex and re-open it. Snapshotting the press-time state lets
  // the click collapse deterministically instead of racing the dismissal (#2621).
  const collapseRoleOnClickRef = useRef(false);

  const canSubmit = isOwner
    ? !!companySize && !!referralSource && !!previousTool && (collectRole ? !!jobRole : true)
    : !!jobRole;

  const handleRoleToggle = () => {
    if (collapseRoleOnClickRef.current) {
      collapseRoleOnClickRef.current = false;
      setOpenIndex(-1);
      return;
    }
    setOpenIndex(openIndex === 1 ? -1 : 1);
  };

  const handleAnswer = (index: number, setter: (v: string) => void, value: string) => {
    setter(value);
    const answers = [companySize, jobRole, referralSource, previousTool];
    answers[index] = value;
    // Skip the role slot (index 1) when it isn't collected, so auto-advance never
    // lands on a hidden question.
    const nextEmptyIndex = answers.findIndex((answer, i) => i > index && answer === null && !(i === 1 && !collectRole));
    setOpenIndex(nextEmptyIndex);
  };

  const handleSkip = () => {
    onCancel();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (collectRole && !jobRole) return;

    const orgId = Number(activeOrganization?.id);
    if (isOwner && !Number.isFinite(orgId)) {
      // No resolvable active org — don't POST to /organization/NaN/firmographics.
      void messageApi.error("Couldn't save — please try again");
      return;
    }

    const firmographics = {
      companySize: companySize as string,
      referralSource: referralSource as string,
      previousTool: previousTool as string,
    };

    let payload: SelfSortSubmitPayload;
    if (!isOwner) {
      payload = { variant: 'member', jobRole: jobRole as string };
    } else if (collectRole) {
      payload = { variant: 'owner', jobRole: jobRole as string, orgId, firmographics };
    } else {
      payload = { variant: 'ownerFirmographics', orgId, firmographics };
    }

    submit.mutate(payload, {
      onSuccess: () => {
        // Role is a per-user answer → Mixpanel user profile property (set once).
        if (collectRole && jobRole) {
          analytics.setUserProperties({ jobRole });
        }
        // Org firmographics are per-org → an org-attributed event, NOT a user
        // property. A user property is single-valued (last write wins), which
        // collapses a multi-org owner's company sizes into one; the event keeps
        // each org's answer, keyed by organizationId.
        if (isOwner) {
          analytics.track(AnalyticsEvent.firmographicsSubmitted, { organizationId: orgId, ...firmographics });
        }
        void messageApi.success({
          content: 'Response sent! Thanks for helping us build a better product',
          style: { marginRight: '20px' },
        });
        onCancel();
      },
      onError: () => {
        void messageApi.error("Couldn't save — please try again");
      },
    });
  };

  return (
    <Modal
      open={open}
      onCancel={handleSkip}
      footer={
        <div className="flex justify-end">
          <BaseButton type="primary" size="medium" disabled={!canSubmit || submit.isPending} onClick={handleSubmit}>
            Submit
          </BaseButton>
        </div>
      }
      closable={!submit.isPending}
      maskClosable={false}
      width={640}
      centered={!isOwner}
      style={isOwner ? { top: 49, paddingBottom: 0 } : { paddingBottom: 0 }}
      styles={{
        content: {
          // 742px is the design height (Figma 15383-22984), measured on the tallest
          // screen. Capped by the viewport so it can't overflow on short displays.
          height: isOwner ? 'min(742px, calc(100vh - 98px))' : '280px',
          display: 'flex',
          flexDirection: 'column',
        },
        body: { flex: 1, minHeight: 0, overflowY: 'auto' },
      }}
      title={isOwner ? 'Help us build a better product for you' : 'One quick question'}
    >
      {contextHolder}
      {isOwner ? (
        <div className="flex flex-col gap-2">
          <AccordionQuestion
            title="What is your company size?"
            selectedLabel={companySize}
            isOpen={openIndex === 0}
            onToggle={() => setOpenIndex(openIndex === 0 ? -1 : 0)}
          >
            <SingleSelectQuestion
              options={COMPANY_SIZE_OPTIONS}
              value={companySize}
              onChange={(v) => handleAnswer(0, setCompanySize, v)}
              layout="segmented"
            />
          </AccordionQuestion>

          {collectRole && (
            <AccordionQuestion
              title="What is your role?"
              selectedLabel={jobRole}
              isOpen={openIndex === 1}
              onToggle={handleRoleToggle}
              onHeaderMouseDown={() => {
                if (openIndex === 1) collapseRoleOnClickRef.current = true;
              }}
            >
              <RoleSelect
                value={jobRole}
                onChange={(v) => handleAnswer(1, setJobRole, v)}
                autoOpen
                onRequestCollapse={() => setOpenIndex(-1)}
              />
            </AccordionQuestion>
          )}

          <AccordionQuestion
            title="How did you hear about Taskonio?"
            selectedLabel={referralSource}
            isOpen={openIndex === 2}
            onToggle={() => setOpenIndex(openIndex === 2 ? -1 : 2)}
          >
            <SingleSelectQuestion
              options={REFERRAL_OPTIONS}
              value={referralSource}
              onChange={(v) => handleAnswer(2, setReferralSource, v)}
              layout="list"
            />
          </AccordionQuestion>

          <AccordionQuestion
            title="Which tool have you used or are currently using?"
            selectedLabel={previousTool}
            isOpen={openIndex === 3}
            onToggle={() => setOpenIndex(openIndex === 3 ? -1 : 3)}
          >
            <SingleSelectQuestion
              options={TOOL_OPTIONS}
              value={previousTool}
              onChange={(v) => handleAnswer(3, setPreviousTool, v)}
              layout="chips"
            />
          </AccordionQuestion>
        </div>
      ) : (
        <AccordionQuestion
          title="What is your role?"
          selectedLabel={jobRole}
          isOpen
          onToggle={() => undefined}
          className="box-border h-[104px]"
        >
          <RoleSelect value={jobRole} onChange={setJobRole} autoOpen />
        </AccordionQuestion>
      )}
    </Modal>
  );
}
